import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { http } from "@/lib/api";

const get = (url) => async () => (await http.get(url)).data;

export const useBillingSettings = () =>
  useQuery({ queryKey: ["billing-settings"], queryFn: get("/billing/settings") });

export const useTenantSubs = () =>
  useQuery({ queryKey: ["billing-tenants"], queryFn: get("/billing/tenants") });

export const useMySubscription = () =>
  useQuery({ queryKey: ["billing-subscription"], queryFn: get("/billing/subscription") });

export const useInvoices = () =>
  useQuery({ queryKey: ["billing-invoices"], queryFn: get("/billing/invoices") });

export const useBillingNotifications = (enabled = true) =>
  useQuery({ queryKey: ["billing-notifications"], queryFn: get("/billing/notifications"), enabled });

export const useMyOrders = () =>
  useQuery({ queryKey: ["billing-orders"], queryFn: get("/billing/orders") });

export const useBillingEvents = () =>
  useQuery({ queryKey: ["billing-events"], queryFn: get("/billing/events") });

export function useBillingActions() {
  const qc = useQueryClient();
  const invalidate = () =>
    ["billing-settings", "billing-tenants", "billing-subscription", "billing-invoices", "billing-orders", "billing-notifications", "billing-events"]
      .forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const saveSettings = useMutation({
    mutationFn: (body) => http.put("/billing/settings", body), onSuccess: invalidate,
  });
  const extendTenant = useMutation({
    mutationFn: ({ tid, body }) => http.post(`/billing/tenants/${tid}/extend`, body), onSuccess: invalidate,
  });
  const tenantAction = useMutation({
    mutationFn: ({ tid, body }) => http.post(`/billing/tenants/${tid}/action`, body), onSuccess: invalidate,
  });
  const createOrder = useMutation({
    mutationFn: (body) => http.post("/billing/orders", body), onSuccess: invalidate,
  });
  const checkOrder = useMutation({
    mutationFn: (id) => http.post(`/billing/orders/${id}/check`), onSuccess: invalidate,
  });
  const submitTx = useMutation({
    mutationFn: ({ id, tx_hash }) => http.post(`/billing/orders/${id}/submit-tx`, { tx_hash }), onSuccess: invalidate,
  });
  const simulate = useMutation({
    mutationFn: ({ id, scenario }) => http.post(`/billing/orders/${id}/simulate-payment`, { scenario }), onSuccess: invalidate,
  });
  const approveOrder = useMutation({
    mutationFn: ({ id, tx_hash }) => http.post(`/billing/orders/${id}/approve`, { tx_hash }), onSuccess: invalidate,
  });
  const rejectOrder = useMutation({
    mutationFn: ({ id, reason }) => http.post(`/billing/orders/${id}/reject`, { reason }), onSuccess: invalidate,
  });
  const expireOrder = useMutation({
    mutationFn: (id) => http.post(`/billing/orders/${id}/expire`), onSuccess: invalidate,
  });
  const markRead = useMutation({
    mutationFn: () => http.post("/billing/notifications/read"), onSuccess: invalidate,
  });
  return { saveSettings, extendTenant, tenantAction, createOrder, checkOrder, submitTx, simulate,
           approveOrder, rejectOrder, expireOrder, markRead };
}
